import { Button } from '@/components/ui/button';
import { type ReactNode, useState } from 'react';

import { EnterRecoveryCodeModal } from '@/components/EnterRecoveryCodeModal';
import { Modal } from '@/components/Modal';
import { EmailConfirmation } from '@/sections/signup/EmailConfirmation';
import { EmailPasswordForm } from '@/sections/signup/EmailPasswordForm';
import { ForgotPasswordSteps } from '@/sections/signup/ForgotPasswordSteps';
import { MFASetup } from '@/sections/signup/MFASetup';
import { OTPInput } from '@/sections/signup/OTPInput';
import { PageWrapper } from '@/sections/signup/PageWrapper';
import { useAuth } from '@/state/auth';

export const Login = () => {
  const { showMFACodeModal, setShowMFACodeModal, verifyMFASetupLogin } =
    useAuth();
  const [totpCode, setTotpCode] = useState('');
  const [rememberDeviceChecked, setRememberDeviceChecked] = useState(false);
  const [showRecoveryCodeModal, setShowRecoveryCodeModal] = useState(false);

  const handleSubmit = async (code?: string) => {
    const codeToSubmit = code || totpCode;
    try {
      setShowMFACodeModal(false);
      await verifyMFASetupLogin(codeToSubmit, rememberDeviceChecked);
    } catch (error) {
      console.error(
        'Error during MFA verification or device remembering:',
        error
      );
    }
  };

  const handleModalClose = () => {
    setShowMFACodeModal(false);
    // Reset state when modal closes
    setTotpCode('');
    setRememberDeviceChecked(false);
  };

  const handleUseRecoveryCode = () => {
    setShowMFACodeModal(false);
    setShowRecoveryCodeModal(true);
  };

  return (
    <PageWrapper title="Sign into the Praetorian Guard Platform">
      <EmailPasswordForm isLogin={true} />
      <Modal
        open={showMFACodeModal}
        footer={{
          hideCancelButton: true,
        }}
        onClose={handleModalClose}
        title="Verify MFA"
        size="xl"
      >
        <div className="text-left">
          {/* Horizontal line */}
          <div className="border-chariot-stroke mb-6 w-full border-t"></div>

          <h4 className="text-chariot-text-primary mb-2 text-xl font-semibold">
            Enter the 6 digit code from authenticator app
          </h4>
        </div>

        {/* Single Column Layout */}
        <div className="mt-6 flex flex-col gap-6">
          {/* Code Verification Section */}

          <div>
            <h3 className="text-chariot-text-primary mb-2 text-sm font-bold">
              Enter the 6 digit code from the app
            </h3>

            <OTPInput
              onSubmit={code => {
                setTotpCode(code);
                if (code.length === 6) {
                  handleSubmit(code);
                }
              }}
            />
          </div>

          <div className="flex justify-between">
            {/* Remember Device Section */}
            {/* <Checkbox
              name="rememberDevice"
              value={rememberDeviceChecked ? 'true' : 'false'}
              onChange={() => setRememberDeviceChecked(!rememberDeviceChecked)}
              label="Remember this device"
              showTick={true}
            /> */}
            <Button
              onClick={handleUseRecoveryCode}
              variant="ghost"
              className="text-chariot-brand-primary ml-auto border-none p-0 text-sm"
            >Use recovery code instead</Button>
          </div>

          {/* Submit Button - Full Width at Bottom */}
          <div className="mt-6">
            <Button
              onClick={() => handleSubmit()}
              disabled={totpCode.length !== 6}
              className="w-full"
            >Submit</Button>
          </div>
        </div>
      </Modal>

      <EnterRecoveryCodeModal
        open={showRecoveryCodeModal}
        onClose={() => setShowRecoveryCodeModal(false)}
      />
    </PageWrapper>
  );
};

export const Signup = ({ description }: { description?: ReactNode }) => {
  const { signupStepIndex, setSignupStepIndex } = useAuth();

  return (
    <PageWrapper title="Sign Up" description={description} showBack={true}>
      <i className="text-sm"> Step {signupStepIndex + 1} of 3</i>
      {signupStepIndex === 0 ? (
        <EmailPasswordForm
          onNext={() => setSignupStepIndex(step => step + 1)}
        />
      ) : signupStepIndex === 1 ? (
        <EmailConfirmation />
      ) : (
        <MFASetup />
      )}
    </PageWrapper>
  );
};

export const ForgotPassword = () => {
  return (
    <PageWrapper
      title="Forgot your password?"
      description="Enter your Email below and we will send a message to reset your password"
      showBack={true}
    >
      <ForgotPasswordSteps />
    </PageWrapper>
  );
};
